import type { CarePlan } from '@/services/CarePlanService/endpoints';

export interface CarePlanInfo {
  conditions: unknown[];
  risk_level: string | null;
  updated_at?: string; // sourced from apiv2 `modified`
  start_date?: string; // sourced from apiv2 `created`
  medication_classes: unknown[];
}

// Newest care plan per patient, keyed by withings_user_id.
// apiv2 returns a row's timestamps as `created`/`modified` (NOT the contract
// `created_at`/`updated_at`, which are absent on reads). Reading the contract names raw
// gave `new Date(undefined)` = Invalid Date → the ">" tiebreak was always false (first-wins,
// not newest-wins) and the Enrolled/Plan cells rendered blank. Read apiv2's fields, keeping
// the app's internal `updated_at`/`start_date` shape for downstream cells. See SPEC §2.
export function buildCarePlanInfoMap(
  rows: CarePlan[] | null | undefined,
): Map<string, CarePlanInfo> {
  const map = new Map<string, CarePlanInfo>();
  (rows || []).forEach((cp) => {
    const uid = String(cp.withings_user_id);
    const updatedAt = (cp.modified ?? cp.updated_at) as string | undefined;
    const createdAt = (cp.created ?? cp.created_at) as string | undefined;
    const prev = map.get(uid);
    if (!prev || new Date(updatedAt ?? 0) > new Date(prev.updated_at ?? 0)) {
      map.set(uid, {
        conditions: (cp.conditions as unknown[]) || [],
        risk_level: (cp.risk_level as string | null) || null,
        updated_at: updatedAt,
        start_date: createdAt,
        medication_classes: (cp.medication_classes as unknown[]) || [],
      });
    }
  });
  return map;
}
